import { ICacheable } from './interfaces/cacheable';
import { Core } from './core';
import { DocumentResource } from './document-resource';
import { DocumentCollection } from './document-collection';
import { Resource } from './resource';

export function isLive(cacheable: ICacheable, ttl?: number): boolean {
    let ttl_in_seconds = typeof ttl === 'number' ? ttl : cacheable.ttl || 0;

    return Date.now() < cacheable.cache_last_update + ttl_in_seconds * 1000;
}

function relationshipPathIsBuilded(resource: Resource | null | undefined, includePath: Array<string>): boolean {
    if (!resource || includePath.length === 0) {
        return true;
    }

    const [relationship_alias, ...nested_path] = includePath;
    const relationship = resource.relationships && resource.relationships[relationship_alias];

    if (!relationship || !relationship.builded) {
        return false;
    }

    if (nested_path.length === 0) {
        return true;
    }

    if (relationship instanceof DocumentResource) {
        return relationship.data ? relationshipPathIsBuilded(relationship.data, nested_path) : true;
    }

    if (relationship instanceof DocumentCollection) {
        return relationship.data.every(related_resource => relationshipPathIsBuilded(<Resource>related_resource, nested_path));
    }

    return false;
}

// @todo test required for hasMany and hasOne
export function relationshipsAreBuilded(resource: Resource, includes: Array<string>): boolean {
    if (includes.length === 0) {
        return true;
    }

    return includes.every(include => relationshipPathIsBuilded(resource, include.split('.')));
}

export function collectionRelationshipsAreBuilded(collection: DocumentCollection, includes: Array<string>): boolean {
    if (includes.length === 0) {
        return true;
    }

    return collection.data.every(resource => relationshipsAreBuilded(resource, includes));
}

/**
 * @deprecated since 2.2.0
 */
export function isCollection(document: DocumentResource | DocumentCollection): document is DocumentCollection {
    if (!document.data) {
        return false;
    }

    return !('id' in document.data);
}

/**
 * @deprecated since 2.2.0
 */
export function isResource(document: DocumentResource | DocumentCollection): document is DocumentResource {
    if (!document.data) {
        return false;
    }

    return 'id' in document.data;
}

// NOTE: Checks that the service passed to the method is registered (method needs to have service's type or a resource as first arg)
// changes "PropertyDescriptor | null" type for "any" to avoid typescript error in decorators property decorators
// (see https://stackoverflow.com/questions/37694322/typescript-ts1241-unable-to-resolve-signature-of-method-decorator-when-called-a)
export function serviceIsRegistered(target: Object, key: string | symbol, descriptor: PropertyDescriptor): any {
    const original = descriptor.value;

    descriptor.value = function() {
        let args = Array.prototype.slice.call(arguments);
        let type: string;
        try {
            if (typeof args[0] === 'string') {
                type = args[0];
            } else {
                type = args[0].type;
            }
        } catch (err) {
            console.warn(`ERROR: First argument of methods decorated with serviceIsRegistered has to be string or Resource.`);

            return null;
        }

        const service_is_registered = Core.me.getResourceService(type);
        if (!service_is_registered) {
            console.warn(`ERROR: ${type} service has not been registered.`);

            return null;
        }

        const result = original.apply(this, args);

        return result;
    };

    return descriptor;
}
